/* ============================================================================
   FullOffice — Product Custom Bundle (consolidado)
   assets/fo-product-custom.js
   ----------------------------------------------------------------------------
   Un solo IIFE 'use strict' con la logica de los 5 bloques de producto.
   - Helpers compartidos: onVisible(), createViewportTimer() y createViewportLoop().
   - Cada bloque se engancha por su selector raiz; si no existe, no hace nada.
   - Nada bloqueante en el tope del archivo: solo se registra el boot() y la
     logica corre diferida cuando cada bloque entra al viewport.
   - Sin librerias externas (no jQuery).
   ========================================================================== */
(function () {
  'use strict';

  /* ==========================================================================
     HELPERS COMPARTIDOS
     ========================================================================== */

  /**
   * onVisible(el, fn, opts)
   * Ejecuta `fn(el)` UNA sola vez cuando `el` entra al viewport.
   * - rootMargin '200px' (arranca un poco antes de ser visible).
   * - Se desconecta tras el primer init (unobserve).
   * - Guard idempotente: marca el elemento con dataset.foVisInit para no
   *   re-inicializar aunque se llame dos veces.
   * - Fallback: si no hay IntersectionObserver, inicializa de inmediato.
   */
  function onVisible(el, fn, opts) {
    if (!el) return;
    if (el.dataset.foVisInit === '1') return;

    function run() {
      if (el.dataset.foVisInit === '1') return;
      el.dataset.foVisInit = '1';
      fn(el);
    }

    if (!('IntersectionObserver' in window)) {
      run();
      return;
    }

    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          run();
          obs.unobserve(entry.target);
          obs.disconnect();
        }
      });
    }, opts || { rootMargin: '200px' });
    io.observe(el);
  }

  /**
   * createViewportTimer(targetEl, tickFn, intervalMs)
   * Timer (setInterval) que respeta la visibilidad:
   * - Solo corre cuando `targetEl` esta en viewport Y la pestana esta visible.
   * - Se detiene (clearInterval) al salir del viewport o con document.hidden.
   * - Reanuda al volver al viewport con la pestana visible.
   * Devuelve un objeto con .start() y .stop() (start arranca la observacion).
   */
  function createViewportTimer(targetEl, tickFn, intervalMs) {
    var timerId = null;
    var inView = false;

    function isRunning() { return timerId !== null; }

    function startTimer() {
      if (isRunning()) return;
      tickFn();                       // primer tick inmediato
      timerId = setInterval(tickFn, intervalMs);
    }

    function stopTimer() {
      if (timerId !== null) {
        clearInterval(timerId);
        timerId = null;
      }
    }

    function evaluate() {
      // Corre solo si esta en viewport y la pestana esta visible.
      if (inView && !document.hidden) {
        startTimer();
      } else {
        stopTimer();
      }
    }

    document.addEventListener('visibilitychange', evaluate);

    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          inView = entry.isIntersecting;
          evaluate();
        });
      }, { rootMargin: '200px' });
    }

    return {
      start: function () {
        if (io) {
          io.observe(targetEl);
        } else {
          inView = true;            // sin IO: asumir visible
          evaluate();
        }
      },
      stop: stopTimer
    };
  }

  /**
   * createViewportLoop(targetEl, tickFn)
   * Igual que createViewportTimer pero para timers ENCADENADOS via setTimeout
   * (no setInterval). El propio tickFn decide el delay del siguiente paso y lo
   * agenda con el `schedule(delayMs)` que recibe. El loop:
   * - Solo corre cuando `targetEl` esta en viewport Y la pestana esta visible.
   * - Pausa (clearTimeout) al salir del viewport o con document.hidden, y
   *   reanuda al volver. Mantiene el estado interno del tickFn (closures),
   *   por lo que la animacion continua donde se quedo.
   * tickFn recibe una funcion `schedule(delayMs)` para encadenar el siguiente
   *   paso. Si tickFn no llama schedule, el loop se detiene hasta el proximo
   *   evento de visibilidad.
   * Devuelve un objeto con .start() y .stop().
   */
  function createViewportLoop(targetEl, tickFn) {
    var timerId = null;
    var inView = false;
    var pendingDelay = null;        // delay del siguiente paso solicitado por tickFn
    var armedDelay = null;          // delay del paso YA armado, para poder reanudarlo

    function isRunning() { return timerId !== null; }

    function schedule(delayMs) {
      // El tickFn pide encadenar el siguiente paso; solo se agenda si
      // seguimos visibles. Si no, se recuerda para reanudar luego.
      pendingDelay = delayMs;
      if (inView && !document.hidden) {
        armTimer();
      }
    }

    function step() {
      timerId = null;
      pendingDelay = null;
      armedDelay = null;
      tickFn(schedule);             // tickFn puede volver a llamar schedule()
    }

    function armTimer() {
      if (isRunning()) return;
      if (pendingDelay === null) return;
      armedDelay = pendingDelay;
      pendingDelay = null;
      timerId = setTimeout(step, armedDelay);
    }

    // Pausa reanudable: devuelve el paso armado a la cola. Sin esto, pausar con
    // el timer ya armado (salir del viewport o esconder la pestana) dejaba
    // pendingDelay en null y el loop no volvia a arrancar jamas al reanudar.
    function pauseTimer() {
      if (timerId !== null) {
        clearTimeout(timerId);
        timerId = null;
        if (pendingDelay === null) pendingDelay = armedDelay;
      }
    }

    function stopTimer() {
      if (timerId !== null) {
        clearTimeout(timerId);
        timerId = null;
      }
      pendingDelay = null;
      armedDelay = null;
    }

    function evaluate() {
      if (inView && !document.hidden) {
        // Reanuda: si habia un paso pendiente, re-armarlo.
        armTimer();
      } else {
        // Pausa: limpia el timeout pero conserva el delay para reanudar.
        pauseTimer();
      }
    }

    document.addEventListener('visibilitychange', evaluate);

    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          inView = entry.isIntersecting;
          evaluate();
        });
      }, { rootMargin: '200px' });
    }

    return {
      start: function (initialDelayMs) {
        pendingDelay = (typeof initialDelayMs === 'number') ? initialDelayMs : 0;
        if (io) {
          io.observe(targetEl);
        } else {
          inView = true;            // sin IO: asumir visible
          evaluate();
        }
      },
      stop: stopTimer
    };
  }


  /* ==========================================================================
     BLOQUE 1 — Badges dinamicos (oferta especial, countdown, descuento)
     Root: #fo-badges-section-unique-2026
     ========================================================================== */
  function initBadges(section) {
    // -- Countdown a medianoche (zona horaria LOCAL del navegador) --
    var countdownBadge = section.querySelector('.fulloffice-countdown-badge');
    var timeDisplay = section.querySelector('.time-display');

    if (countdownBadge && timeDisplay) {
      function buildTime() {
        var now = new Date();
        var midnight = new Date(now);
        midnight.setHours(24, 0, 0, 0);    // siguiente dia a las 00:00:00
        var diff = midnight - now;
        if (diff <= 0) return '00:00:00';

        var hours = Math.floor(diff / (1000 * 60 * 60));
        var minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        var seconds = Math.floor((diff % (1000 * 60)) / 1000);

        return String(hours).padStart(2, '0') + ':' +
               String(minutes).padStart(2, '0') + ':' +
               String(seconds).padStart(2, '0');
      }

      // Una sola escritura al DOM por tick (sin reflows extra).
      // El timer compartido pausa fuera del viewport / con pestana oculta.
      var cdTimer = createViewportTimer(countdownBadge, function () {
        timeDisplay.textContent = buildTime();
      }, 1000);
      cdTimer.start();
    }

    // -- Insignia dinamica por vistas / inventario / marca --
    var badgeContainer = section.querySelector('.fo-dynamic-badge');
    if (badgeContainer) {
      var productId = badgeContainer.getAttribute('data-product-id');
      var isTopBrand = badgeContainer.getAttribute('data-is-top-brand') === 'true';
      var hasHighInventory = badgeContainer.getAttribute('data-high-inventory') === 'true';

      // localStorage: UNA escritura por carga, con try/catch.
      var viewCount = 0;
      try {
        var viewHistory = JSON.parse(localStorage.getItem('fo_product_views') || '{}');
        viewCount = (viewHistory[productId] || 0) + 1;
        viewHistory[productId] = viewCount;
        localStorage.setItem('fo_product_views', JSON.stringify(viewHistory));
      } catch (e) {
        viewCount = 1;
      }

      var selectedBadge = null;
      if (viewCount > 2) {
        selectedBadge = { text: 'Mejor Opción', cls: 'fo-badge-best' };
      } else if (hasHighInventory) {
        selectedBadge = { text: 'Opción FullOffice.com', cls: 'fo-badge-fulloffice' };
      } else if (isTopBrand) {
        selectedBadge = { text: 'Marca Certificada', cls: 'fo-badge-certified' };
      }

      if (selectedBadge) {
        badgeContainer.innerHTML =
          '<span class="fo-badge-base ' + selectedBadge.cls + '">' + selectedBadge.text + '</span>';
      }
    }

    // -- Badge de descuento animado (typewriter, corre al entrar en viewport) --
    var discountBadgeContainer = section.querySelector('.fulloffice-discount-badge');
    var discountBadge = section.querySelector('.badge-typewriter');

    if (discountBadge && discountBadgeContainer) {
      var discount = discountBadgeContainer.getAttribute('data-discount');
      var texts = ['Oferta Especial', discount];

      var currentText = '';
      var isDeleting = false;
      var textIndex = 0;
      var charIndex = 0;
      var isPaused = false;
      var isEmpty = false;          // refleja la clase fo-badge-empty actual (evita reflow por tick)

      // Estado inicial: contenedor vacio -> oculto via clase CSS (no style.display).
      discountBadgeContainer.classList.add('fo-badge-empty');
      isEmpty = true;

      // Solo togglea la clase en las TRANSICIONES vacio<->no-vacio (no por caracter),
      // evitando reflows en el hot path.
      function setEmpty(empty) {
        if (empty === isEmpty) return;
        isEmpty = empty;
        if (empty) {
          discountBadgeContainer.classList.add('fo-badge-empty');
        } else {
          discountBadgeContainer.classList.remove('fo-badge-empty');
        }
      }

      // type(schedule): un paso del typewriter. Escribe textContent (una sola
      // escritura al DOM en el hot path) y pide el siguiente paso via schedule.
      // El loop compartido (createViewportLoop) pausa/limpia el timeout fuera
      // del viewport o con document.hidden.
      function type(schedule) {
        var fullText = texts[textIndex];

        if (isPaused) {
          isPaused = false;
          schedule(500);
          return;
        }

        if (isDeleting) {
          currentText = fullText.substring(0, charIndex - 1);
          charIndex--;
        } else {
          currentText = fullText.substring(0, charIndex + 1);
          charIndex++;
        }

        discountBadge.textContent = currentText;
        setEmpty(currentText === '');

        var typeSpeed = isDeleting ? 50 : 100;

        if (!isDeleting && charIndex === fullText.length) {
          typeSpeed = 2000;
          isDeleting = true;
        } else if (isDeleting && charIndex === 0) {
          setEmpty(true);
          isDeleting = false;
          textIndex = (textIndex + 1) % texts.length;
          isPaused = true;
          typeSpeed = 500;
        }

        schedule(typeSpeed);
      }

      // Loop que respeta viewport + document.hidden: arranca al entrar al
      // viewport y se pausa/limpia al salir o con la pestana en background.
      // IMPORTANTE: se observa `section` (siempre maquetada), NO el propio
      // discountBadgeContainer. El badge arranca con .fo-badge-empty
      // (display:none) y un elemento display:none NUNCA es isIntersecting para
      // IntersectionObserver (caja 0x0) -> el loop jamas arrancaria y el badge
      // quedaria oculto para siempre (deadlock). La section nunca es display:none.
      var twLoop = createViewportLoop(section, type);
      twLoop.start(500);            // primer paso tras 500ms (igual que antes)
    }
  }


  /* ==========================================================================
     BLOQUE 2 — Envio gratis / express (slider CSS-only)
     Root: #fo-shipping-block-2026  (slider interno: [data-fo-slider])
     ========================================================================== */
  function initShipping(root) {
    var slider = root.querySelector('[data-fo-slider]');
    if (!slider) return; // variante sin slider (envio normal / gratis): nada que animar

    // El CSS anima el slider POR DEFECTO (resiliente sin este JS, como el bloque original).
    // Aqui solo lo PAUSAMOS fuera de viewport / con la pestana en background, anadiendo
    // .fo-slider-paused (-> animation-play-state: paused). Nunca quitamos la animacion en si.
    var inView = true; // por defecto en vista; el IO la corrige de inmediato si arranca fuera

    function sync() {
      var paused = !inView || document.hidden;
      slider.classList.toggle('fo-slider-paused', paused);
    }

    document.addEventListener('visibilitychange', sync);

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { inView = e.isIntersecting; });
        sync();
      }, { rootMargin: '200px' });
      io.observe(slider);
    }
    // Sin IntersectionObserver: inView se queda true y la animacion corre siempre (como el original).
  }


  /* ==========================================================================
     BLOQUE 4 — Precio por volumen (cards horizontales)
     Root: .vp2-section
     ========================================================================== */
  function initVp2(section) {
    var listPrice = parseInt(section.dataset.listPrice, 10) || 0;
    var cards = section.querySelectorAll('#vp2-cards .vp2-card');
    var savingsEl = section.querySelector('#vp2-savings');
    var QTY_SELECTOR = 'input[name="quantity"], .quantity__input, input.qty, input[data-quantity-input]';
    var BTN_SELECTOR = '.quantity__button, [data-quantity-plus], [data-quantity-minus], button[name="plus"], button[name="minus"]';
    var listenedInputs = new WeakSet();
    var listenedBtns = new WeakSet();

    // Contenedor de controles de cantidad: acota el scope del MutationObserver
    // y de las busquedas de botones. Se resuelve al control mas cercano al
    // input de cantidad; si no existe aun, cae a un contenedor de producto
    // acotado (nunca main/body con subtree global).
    var controlsRoot = null;

    function resolveControlsRoot() {
      var input = document.querySelector(QTY_SELECTOR);
      if (input) {
        // Sube al wrapper de cantidad mas cercano (quantity selector del tema).
        var scope = input.closest('.quantity, .quantity-selector, [data-quantity], .product-form__quantity, .product-form');
        if (scope) return scope;
      }
      // Fallback acotado: formulario de producto o la propia seccion vp2.
      return document.querySelector('product-form, form[action*="/cart/add"], .product-form') || section;
    }

    function getControlsRoot() {
      // Re-resuelve si el nodo cacheado salio del DOM (tema re-renderizo).
      if (!controlsRoot || !controlsRoot.isConnected) {
        controlsRoot = resolveControlsRoot();
      }
      return controlsRoot;
    }

    function getInput() { return document.querySelector(QTY_SELECTOR); }
    function getQty() { var i = getInput(); return i ? parseInt(i.value, 10) || 1 : 1; }

    function formatMXN(cents) {
      var amount = cents / 100;
      return '$' + amount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    function updateSavings(activeCard) {
      if (!savingsEl) return;
      var tierPrice = parseInt(activeCard.dataset.tierPrice, 10);
      var qty = parseInt(activeCard.dataset.qty, 10);
      var savings = (listPrice - tierPrice) * qty;
      savingsEl.textContent = savings > 0 ? '-' + formatMXN(savings) : '-$0.00';
    }

    function updateCards(qty) {
      var activeCard = null;
      cards.forEach(function (c) {
        var active = qty >= parseInt(c.dataset.min, 10) && qty <= parseInt(c.dataset.max, 10);
        c.classList.toggle('vp2-active', active);
        if (active) activeCard = c;
      });
      if (activeCard) updateSavings(activeCard);
    }

    function setQty(qty) {
      qty = Math.max(1, qty);
      var i = getInput();
      if (i) {
        i.value = qty;
        i.dispatchEvent(new Event('change', { bubbles: true }));
        i.dispatchEvent(new Event('input', { bubbles: true }));
      }
      updateCards(qty);
    }

    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        if (!card.classList.contains('vp2-active')) {
          setQty(parseInt(card.dataset.qty, 10));
        }
      });
    });

    // Fix scroll horizontal en mobile: detectar direccion del swipe.
    var wrapper = section.querySelector('.vp2-cards-wrapper');
    if (wrapper) {
      var startX = 0, startY = 0, isHorizontal = null;
      wrapper.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isHorizontal = null;
      }, { passive: true });
      wrapper.addEventListener('touchmove', function (e) {
        var dx = Math.abs(e.touches[0].clientX - startX);
        var dy = Math.abs(e.touches[0].clientY - startY);
        if (isHorizontal === null) { isHorizontal = dx > dy; }
        if (isHorizontal) { e.stopPropagation(); }
      }, { passive: true });
    }

    // Engancha listeners a los controles de cantidad. Acotado al contenedor de
    // controles (no querySelectorAll global): el input via QTY_SELECTOR (suele
    // ser unico) y los botones SOLO dentro de controlsRoot.
    function attachThemeListeners() {
      var i = getInput();
      if (i && !listenedInputs.has(i)) {
        listenedInputs.add(i);
        i.addEventListener('change', function () { updateCards(getQty()); });
        i.addEventListener('input', function () { updateCards(getQty()); });
      }
      var scope = getControlsRoot();
      scope.querySelectorAll(BTN_SELECTOR).forEach(function (btn) {
        if (!listenedBtns.has(btn)) {
          listenedBtns.add(btn);
          btn.addEventListener('click', function () {
            setTimeout(function () { updateCards(getQty()); }, 60);
          });
        }
      });
    }

    // Throttle de attachThemeListeners para el callback del MutationObserver:
    // en paginas con sliders/variantes que mutan DOM con frecuencia evita
    // re-escanear en cada mutacion (coalesce via requestAnimationFrame).
    var raf = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };
    var pendingScan = null;
    function scheduleAttach() {
      if (pendingScan !== null) return;
      pendingScan = raf(function () {
        pendingScan = null;
        attachThemeListeners();
      });
    }

    // Sincroniza con eventos nativos del tema en vez de polling continuo.
    document.addEventListener('on:variant:change', function () { updateCards(getQty()); });
    document.addEventListener('on:cart:add', function () { updateCards(getQty()); });

    // Estado inicial + enganche a controles existentes del tema.
    updateCards(getQty());
    attachThemeListeners();

    // MutationObserver ACOTADO al contenedor de cantidad (no main/body). Su
    // callback es throttleado (scheduleAttach) y solo consulta dentro del
    // contenedor de controles. observing=flag para re-observe idempotente.
    var moTarget = getControlsRoot();
    var mo = new MutationObserver(scheduleAttach);
    var observing = false;

    function startObserving() {
      if (observing) return;        // idempotente: no re-armar si ya activo
      moTarget = getControlsRoot(); // re-resolver por si el tema re-renderizo
      mo.observe(moTarget, { childList: true, subtree: true });
      observing = true;
    }

    function stopObserving() {
      if (!observing) return;
      mo.disconnect();
      observing = false;
    }

    startObserving();

    // Pausa el observer cuando la seccion no esta visible; reanuda al volver.
    if ('IntersectionObserver' in window) {
      var pauseObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            startObserving();        // idempotente: no re-arma trabajo redundante
            attachThemeListeners();
          } else {
            stopObserving();
          }
        });
      }, { rootMargin: '200px' });
      pauseObs.observe(section);
    }

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) { updateCards(getQty()); }
    });
  }


  /* ==========================================================================
     BLOQUE 5 — Banner PayPal (carrusel automatico de 2 mensajes)
     Root: #fo-paypal-unique-section-2026
     Slide 1: PayPal hasta 18 meses. Slide 2: "Crea tu propia cotizacion en un
     clic" con el contorno iluminado. El desplazamiento es un translateX sobre
     el track (el CSS hace el resto); aqui solo se lleva el indice y se marca
     .is-active, que es lo que enciende el contorno del slide 2.
     El loop se pausa fuera de viewport y con la pestana oculta.
     ========================================================================== */
  function initPaypal(section) {
    var track = section.querySelector('[data-fo-pp-track]');
    if (!track) return;

    var slides = Array.prototype.slice.call(section.querySelectorAll('[data-fo-pp-slide]'));
    if (slides.length < 2) return;

    // Tiempo que cada mensaje permanece al frente. El de cotizacion dura algo
    // mas para que de tiempo a notar el contorno encendiendose.
    var DWELL_MS = [5000, 6500];
    var index = 0;

    function render() {
      track.style.transform = 'translateX(-' + (index * 100) + '%)';
      for (var i = 0; i < slides.length; i++) {
        var isActive = (i === index);
        if (isActive) {
          slides[i].classList.add('is-active');
        } else {
          slides[i].classList.remove('is-active');
        }
        slides[i].setAttribute('aria-hidden', isActive ? 'false' : 'true');
      }
    }

    render();

    var loop = createViewportLoop(section, function (schedule) {
      index = (index + 1) % slides.length;
      render();
      schedule(DWELL_MS[index] || DWELL_MS[0]);
    });

    loop.start(DWELL_MS[0]);
  }


  /* ==========================================================================
     BOOT — registra cada bloque por su selector raiz (guard si no existe)
     ========================================================================== */
  function boot() {
    var badges = document.getElementById('fo-badges-section-unique-2026');
    if (badges) onVisible(badges, initBadges);

    var shipping = document.getElementById('fo-shipping-block-2026');
    if (shipping) onVisible(shipping, initShipping);

    // Puede haber multiples instancias de .vp2-section en teoria.
    var vp2List = document.querySelectorAll('.vp2-section');
    vp2List.forEach(function (s) { onVisible(s, initVp2); });

    var paypal = document.getElementById('fo-paypal-unique-section-2026');
    if (paypal) onVisible(paypal, initPaypal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();